import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../../utils/apiConfig';
import './TrendingCoinsWidget.css';

/**
 * Compact widget to display Top Gainers (coins with highest 24h price change)
 * Used on CoinPage sidebar
 */
const TrendingCoinsWidget = ({ limit = 4 }) => {
  const { t } = useTranslation();
  const [coins, setCoins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Fetch with retry logic to handle occasional HTML responses (CDN/cache issues)
    const fetchWithRetry = async (url, retries = 3) => {
      for (let i = 0; i < retries; i++) {
        try {
          const res = await fetch(url);
          if (!res.ok) {
            if (i < retries - 1) {
              await new Promise(r => setTimeout(r, 1000 * (i + 1)));
              continue;
            }
            throw new Error(`HTTP ${res.status}`);
          }

          const text = await res.text();
          // Check if response is HTML instead of JSON (CDN/cache issue)
          if (text.startsWith('<!DOCTYPE') || text.startsWith('<html')) {
            console.warn(`[TrendingCoinsWidget] Received HTML instead of JSON (attempt ${i + 1}/${retries})`);
            if (i < retries - 1) {
              await new Promise(r => setTimeout(r, 1000 * (i + 1)));
              continue;
            }
            throw new Error('Received HTML instead of JSON');
          }

          return JSON.parse(text);
        } catch (e) {
          if (i === retries - 1) throw e;
          await new Promise(r => setTimeout(r, 1000 * (i + 1)));
        }
      }
    };

    const fetchTrending = async () => {
      try {
        // Add cache-busting parameter to bypass Cloudflare cache
        const data = await fetchWithRetry(`${API_BASE_URL}/api/bubbles?mode=top100&_t=${Date.now()}`);

        if (!data.data || !Array.isArray(data.data)) {
          setCoins([]);
          return;
        }

        // Filter for positive changes and sort by change (descending)
        const topGainers = data.data
          .filter(c => c.change != null && c.change > 0)
          .sort((a, b) => b.change - a.change)
          .slice(0, limit);

        setCoins(topGainers);
        setError(null); // Clear previous error on success
      } catch (err) {
        console.error('Failed to fetch trending coins:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTrending();

    // Refresh every 60 seconds
    const interval = setInterval(fetchTrending, 60000);
    return () => clearInterval(interval);
  }, [limit]);

  // Format price
  const formatPrice = (price) => {
    if (price === undefined || price === null) return '$0';
    if (price < 0.01) return `$${price.toFixed(6)}`;
    if (price < 1) return `$${price.toFixed(4)}`;
    if (price < 100) return `$${price.toFixed(2)}`;
    return `$${price.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  };

  if (loading) {
    return (
      <div className="trending-coins-widget">
        <h3 className="trending-title">{t('trendingCoins.title', 'Trending Coins')}</h3>
        <div className="trending-loading">{t('common.loading', 'Loading...')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="trending-coins-widget">
        <h3 className="trending-title">{t('trendingCoins.title', 'Trending Coins')}</h3>
        <div className="trending-error">{t('trendingCoins.error', 'Failed to load')}</div>
      </div>
    );
  }

  if (coins.length === 0) {
    return (
      <div className="trending-coins-widget">
        <h3 className="trending-title">{t('trendingCoins.title', 'Trending Coins')}</h3>
        <div className="trending-empty">{t('trendingCoins.empty', 'No trending coins available')}</div>
      </div>
    );
  }

  return (
    <div className="trending-coins-widget">
      <h3 className="trending-title">{t('trendingCoins.title', 'Trending Coins')}</h3>

      <div className="trending-coins-grid">
        {coins.map((coin) => (
          <Link
            key={coin.symbol}
            to={`/price/${coin.symbol}`}
            className="trending-coin-card"
          >
            <div className="trending-coin-icon">
              {coin.image ? (
                <img src={coin.image} alt={coin.symbol} />
              ) : (
                <div className="trending-coin-placeholder">
                  {coin.symbol?.charAt(0)}
                </div>
              )}
            </div>
            <span className="trending-coin-symbol">{coin.symbol}</span>
            <span className="trending-coin-price">{formatPrice(coin.price)}</span>
            <span className="trending-coin-change">
              +{coin.change?.toFixed(1)}%
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default TrendingCoinsWidget;
